import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Outlet } from 'react-router-dom';

import { Sheet, SheetContent, SheetHeader, SheetTitle } from '@/components/ui/sheet';
import { cn } from '@/lib/utils';
import { AppFooter } from './AppFooter';
import { Header } from './Header';
import { SidebarNav } from './SidebarNav';

/**
 * Authenticated app shell: sticky top header, a persistent sidebar on `lg+`
 * that collapses to a slide-in drawer on smaller screens, and the routed
 * content in <Outlet />.
 */
export function AppShell() {
  const { t } = useTranslation('common');
  const [mobileNavOpen, setMobileNavOpen] = useState(false);
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);

  return (
    <div className="flex min-h-screen flex-col">
      <Header
        onMenuClick={() => setMobileNavOpen(true)}
        sidebarCollapsed={sidebarCollapsed}
        onToggleSidebar={() => setSidebarCollapsed(c => !c)}
      />

      <div className="flex flex-1">
        {/* Desktop sidebar — grey bg-sidebar surface framing the content canvas. */}
        <aside
          className={cn(
            'bg-sidebar text-sidebar-foreground border-sidebar-border hidden shrink-0 border-r lg:block',
            sidebarCollapsed ? 'w-0 overflow-hidden border-r-0' : 'w-64'
          )}
        >
          <div className="sticky top-14 h-[calc(100vh-3.5rem)]">
            <SidebarNav />
          </div>
        </aside>

        {/* Mobile drawer */}
        <Sheet open={mobileNavOpen} onOpenChange={setMobileNavOpen}>
          <SheetContent side="left" className="bg-sidebar text-sidebar-foreground w-72 p-0">
            <SheetHeader className="sr-only">
              <SheetTitle>{t('navigation')}</SheetTitle>
            </SheetHeader>
            <SidebarNav onNavigate={() => setMobileNavOpen(false)} />
          </SheetContent>
        </Sheet>

        <main className="min-w-0 flex-1 p-4 md:p-6">
          <Outlet />
        </main>
      </div>

      <AppFooter />
    </div>
  );
}
